import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiBox, FiLayers, FiDollarSign, FiCheckCircle, 
  FiAlertTriangle, FiTrendingUp, FiCpu, FiDatabase,
  FiShield, FiActivity
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

const SummaryView = () => {
  const { state } = useSolution();
  
  const phaseStats = {};
  state.selectedComponents.forEach(comp => {
    const key = comp.phaseName || 'Unknown';
    if (!phaseStats[key]) {
      phaseStats[key] = { count: 0, icon: comp.phaseIcon || '📦', layers: new Set() };
    }
    phaseStats[key].count++;
    phaseStats[key].layers.add(comp.layerId);
  });
  
  return (
    <div className="h-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Solution Summary</h2>
        
        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: FiBox, label: 'Total Components', value: state.selectedComponents.length, color: 'text-blue-500', bg: 'bg-blue-50' },
            { icon: FiLayers, label: 'Layers Covered', value: new Set(state.selectedComponents.map(c => c.layerId)).size, color: 'text-purple-500', bg: 'bg-purple-50' },
            { icon: FiDollarSign, label: 'Est. Cost Range', value: `$${state.totalCost.low.toFixed(0)} - $${state.totalCost.high.toFixed(0)}`, color: 'text-green-500', bg: 'bg-green-50' },
            { icon: FiActivity, label: 'Sub-Layers', value: new Set(state.selectedComponents.map(c => c.subLayerId)).size, color: 'text-orange-500', bg: 'bg-orange-50' }
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`${stat.bg} rounded-xl p-6 text-center`}
            >
              <stat.icon className={`w-8 h-8 mx-auto mb-3 ${stat.color}`} />
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
        
        {/* Phase Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Phase Breakdown</h3>
          <div className="space-y-4">
            {Object.entries(phaseStats).map(([phase, data], i) => (
              <motion.div
                key={phase}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{data.icon}</span>
                  <div>
                    <div className="font-semibold text-gray-900">{phase}</div>
                    <div className="text-sm text-gray-500">{data.layers.size} layers</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{data.count}</div>
                  <div className="text-xs text-gray-500">components</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Validation Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Validation Status</h3>
          <div className={`p-4 rounded-lg ${state.validation.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-3">
              {state.validation.isValid ? (
                <FiCheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <FiAlertTriangle className="w-6 h-6 text-red-500" />
              )}
              <span className={`font-semibold ${state.validation.isValid ? 'text-green-700' : 'text-red-700'}`}>
                {state.validation.isValid ? 'Solution is Valid' : `${state.validation.issues.length} Issues Found`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryView;